import { useCallback, useEffect, useMemo, useState } from "react";
import { PerformanceMetric } from "../../../services/performanceMetricsService";
import { ChartDataPoint, PerformanceTimeframe } from "./types";

interface UsePortfolioPerformanceOptions {
  performanceMetrics: PerformanceMetric[];
  createdAt?: string | null;
}

interface UsePortfolioPerformanceReturn {
  selectedPeriod: PerformanceTimeframe;
  setSelectedPeriod: (period: PerformanceTimeframe) => void;
  availableFilters: PerformanceTimeframe[];
  orderedFiltersForTable: PerformanceTimeframe[];
  chartData: ChartDataPoint[];
  timeframePerformance: Record<string, number | null>;
  oneYearPerformance: number | null;
  formattedCreateDate: string | null;
  handleSelectPeriod: (period: string) => void;
}

export function usePortfolioPerformance({
  performanceMetrics,
  createdAt,
}: UsePortfolioPerformanceOptions): UsePortfolioPerformanceReturn {
  const [selectedPeriod, setSelectedPeriod] = useState<PerformanceTimeframe>("1 year");
  const [userOverrodePeriod, setUserOverrodePeriod] = useState(false);

  // Helper to check if a filter has data
  const hasDataForFilter = useCallback(
    (filter: string, metrics: PerformanceMetric[]) => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (filter) {
        case "All":
          return metrics.length > 0;
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
        }
        case "Since inception": {
          if (!createdAt) return metrics.length > 0;
          const inceptionDate = new Date(createdAt);
          inceptionDate.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= inceptionDate;
          });
        }
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
        }
        case "Month to date": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
        }
        default:
          return metrics.length > 0;
      }
    },
    [createdAt]
  );

  // Compute available filter options based on data availability
  const availableFilters = useMemo(() => {
    if (performanceMetrics.length === 0) return ["Since inception"] as PerformanceTimeframe[];

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return ["Since inception"] as PerformanceTimeframe[];

    const allFilters: PerformanceTimeframe[] = [
      "1 week",
      "1 month",
      "3 months",
      "6 months",
      "Year to date",
      "1 year",
      "Since inception",
      "All",
    ];

    return allFilters.filter(filter => hasDataForFilter(filter, factorMetrics));
  }, [performanceMetrics, hasDataForFilter]);

  // Ensure prominent ordering for stats table: All, Since inception first
  const orderedFiltersForTable = useMemo(() => {
    const specials: PerformanceTimeframe[] = ["All", "Since inception"];
    const presentSpecials = specials.filter(f => availableFilters.includes(f));
    const rest = availableFilters.filter(f => !specials.includes(f));
    return [...presentSpecials, ...rest] as PerformanceTimeframe[];
  }, [availableFilters]);

  // Ensure selected period is valid; auto-promote to 1Y when it becomes available until user overrides
  useEffect(() => {
    if (availableFilters.length === 0) return;
    if (!userOverrodePeriod && availableFilters.includes("1 year") && selectedPeriod !== "1 year") {
      setSelectedPeriod("1 year");
      return;
    }
    if (!availableFilters.includes(selectedPeriod)) {
      const preferred: PerformanceTimeframe[] = [
        "1 year",
        "Year to date",
        "6 months",
        "3 months",
        "1 month",
        "1 week",
        "Since inception",
        "All",
      ];
      const next = preferred.find(f => availableFilters.includes(f)) || availableFilters[0];
      if (next) setSelectedPeriod(next);
    }
  }, [availableFilters, selectedPeriod, userOverrodePeriod]);

  const handleSelectPeriod = (value: string) => {
    setUserOverrodePeriod(true);
    setSelectedPeriod(value as PerformanceTimeframe);
  };

  // Filter metrics by selected time period
  const filterMetricsByPeriod = useCallback(
    (metrics: PerformanceMetric[]) => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (selectedPeriod) {
        case "All":
          return metrics;
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
        }
        case "Since inception": {
          if (!createdAt) return metrics;
          const inceptionDate = new Date(createdAt);
          inceptionDate.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= inceptionDate;
          });
        }
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
        }
        default:
          return metrics;
      }
    },
    [selectedPeriod, createdAt]
  );

  // Format performance data for chart (factor-based cumulative %)
  const chartData = useMemo(() => {
    if (performanceMetrics.length === 0) return [];

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return [];

    const filtered = filterMetricsByPeriod(factorMetrics);
    if (filtered.length === 0) return [];

    const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

    // Build cumulative series and always prepend a 0% baseline at the first visible date
    let cumulative = 1;
    const cumulativePoints: ChartDataPoint[] = sorted.map(m => {
      cumulative *= m.metricValue;
      return { date: m.metricDate.split("T")[0], value: (cumulative - 1) * 100 };
    });

    if (sorted.length > 0) {
      const firstDateStr = sorted[0].metricDate.split("T")[0];
      const [year, month, day] = firstDateStr.split("-").map(Number);
      const baselineDate = new Date(Date.UTC(year, month - 1, day - 1));
      const baselineDateStr = baselineDate.toISOString().split("T")[0];
      return [{ date: baselineDateStr, value: 0 }, ...cumulativePoints];
    }

    return cumulativePoints;
  }, [performanceMetrics, filterMetricsByPeriod]);

  // Calculate performance for each available timeframe
  const timeframePerformance = useMemo(() => {
    if (performanceMetrics.length === 0) return {};

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return {};

    const metrics: Record<string, number | null> = {};
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const calculateCumulativePerformance = (filtered: PerformanceMetric[]): number | null => {
      if (filtered.length === 0) return null;
      const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());
      let cumulative = 1;
      sorted.forEach(m => {
        cumulative *= m.metricValue;
      });
      return (cumulative - 1) * 100;
    };

    availableFilters.forEach(filter => {
      let filtered: PerformanceMetric[] = [];

      switch (filter) {
        case "All":
          filtered = factorMetrics;
          break;
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
          break;
        }
        case "Since inception":
          if (createdAt) {
            const inceptionDate = new Date(createdAt);
            inceptionDate.setHours(0, 0, 0, 0);
            filtered = factorMetrics.filter(m => {
              const metricDate = new Date(m.metricDate);
              metricDate.setHours(0, 0, 0, 0);
              return metricDate >= inceptionDate;
            });
          } else {
            filtered = factorMetrics;
          }
          break;
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
          break;
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
          break;
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
          break;
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
          break;
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
          break;
        }
        default:
          filtered = factorMetrics;
      }

      metrics[filter] = calculateCumulativePerformance(filtered);
    });

    return metrics;
  }, [performanceMetrics, availableFilters, createdAt]);

  // Get 1Y cumulative performance
  const oneYearPerformance = useMemo(() => {
    if (performanceMetrics.length === 0) return null;

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return null;

    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    oneYearAgo.setHours(0, 0, 0, 0);

    const filtered = factorMetrics.filter(m => {
      const metricDate = new Date(m.metricDate);
      metricDate.setHours(0, 0, 0, 0);
      return metricDate >= oneYearAgo;
    });

    if (filtered.length === 0) return null;
    const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());
    let cumulative = 1;
    sorted.forEach(m => {
      cumulative *= m.metricValue;
    });
    return (cumulative - 1) * 100;
  }, [performanceMetrics]);

  // Get formatted create date for chart reference point
  const formattedCreateDate = useMemo(() => {
    if (!createdAt) return null;
    return createdAt.split("T")[0];
  }, [createdAt]);

  return {
    selectedPeriod,
    setSelectedPeriod,
    availableFilters,
    orderedFiltersForTable,
    chartData,
    timeframePerformance,
    oneYearPerformance,
    formattedCreateDate,
    handleSelectPeriod,
  };
}
